"use client";

import { useState, useEffect } from "react";
import Image from "next/image";
import { Star, Camera, X, CheckCircle, CornerDownRight } from "lucide-react";

interface ProductReviewSectionProps {
  productId: number;
  productName?: string;
  subdomain: string;
}

export default function ProductReviewSection({
  productId,
  productName = "",
  subdomain,
}: ProductReviewSectionProps) {
  const [reviews, setReviews] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showReviewForm, setShowReviewForm] = useState(false);

  // অটো-ফিল স্টেটস
  const [rating, setRating] = useState(5);
  const [hoverRating, setHoverRating] = useState(0);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [authorName, setAuthorName] = useState("");
  const [imageFiles, setImageFiles] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const cleanSub = (subdomain || "nextlook").trim().toLowerCase();
  const apiBaseUrl = `https://${cleanSub}.shop.xylotechsolution.com/api/v1/reviews/`;

  // 🌟 ১. কাস্টমারের নাম এবং প্রোডাক্টের নাম অটোমেটিক লোড করা[cite: 47]
  useEffect(() => {
    if (typeof window !== "undefined") {
      const savedUserStr = localStorage.getItem("user") || sessionStorage.getItem("user");
      let detectedName = "";

      if (savedUserStr) {
        try {
          const parsedUser = JSON.parse(savedUserStr);
          detectedName =
            parsedUser.name ||
            parsedUser.full_name ||
            parsedUser.username ||
            parsedUser.first_name ||
            parsedUser.email?.split("@")[0] ||
            "";
        } catch (_) {}
      }

      if (!detectedName) {
        detectedName =
          localStorage.getItem("customer_name") ||
          localStorage.getItem("user_name") ||
          localStorage.getItem("name") ||
          "";
      }

      if (detectedName) {
        setAuthorName(detectedName);
      }
    }

    if (productName) {
      setTitle(`Review for ${productName}`);
    }
  }, [productName]);

  const fetchReviews = async () => {
    try {
      setIsLoading(true);
      const res = await fetch(`${apiBaseUrl}?product=${productId}`, {
        headers: {
          Accept: "application/json",
          "x-tenant-id": cleanSub,
        },
      });
      if (res.ok) {
        const data = await res.json();
        const list = Array.isArray(data) ? data : data.results || [];
        setReviews(list);
      }
    } catch (err) {
      console.error("Failed to load reviews", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (productId) {
      fetchReviews();
    }
  }, [productId]);

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const files = Array.from(e.target.files);
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (reader.result) {
          setImageFiles((prev) => [...prev, reader.result as string]);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index: number) => {
    setImageFiles((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleSubmitReview = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!content.trim()) return;

    try {
      setIsSubmitting(true);
      const token = typeof window !== "undefined" ? localStorage.getItem("access_token") : "";

      const payload = {
        product: productId,
        rating: rating,
        title: title || (productName ? `Review for ${productName}` : "Customer Review"),
        content: content,
        images: imageFiles,
        is_anonymous: !authorName.trim(),
      };

      const res = await fetch(apiBaseUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "x-tenant-id": cleanSub,
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      });

      if (res.ok || res.status === 201) {
        setContent("");
        setImageFiles([]);
        setShowReviewForm(false);
        await fetchReviews();
      } else {
        const errJson = await res.json().catch(() => ({}));
        alert(errJson.detail || "Review submit failed. Please try again.");
      }
    } catch (err) {
      console.error(err);
      alert("Network connection error!");
    } finally {
      setIsSubmitting(false);
    }
  };

  const totalReviews = reviews.length;
  const avgRating =
    totalReviews > 0
      ? (reviews.reduce((acc, r) => acc + (r.rating || 5), 0) / totalReviews).toFixed(1)
      : "5.0";

  return (
    <section className="space-y-10">
      {/* রেটিং সারাংশ */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 p-6 sm:p-8 bg-[#F8FAFC] border border-slate-100 rounded-3xl items-center">
        <div className="text-center md:border-r border-slate-200 md:pr-8 space-y-2">
          <span className="text-5xl font-black text-slate-900">{avgRating}</span>
          <div className="flex justify-center gap-1 text-amber-400">
            {[...Array(5)].map((_, i) => (
              <Star
                key={i}
                className={`w-5 h-5 ${
                  i < Math.round(Number(avgRating)) ? "fill-amber-400 text-amber-400" : "text-slate-300"
                }`}
              />
            ))}
          </div>
          <p className="text-xs text-slate-500 font-medium">Based on {totalReviews} reviews</p>
        </div>

        <div className="space-y-2">
          {[5, 4, 3, 2, 1].map((star) => {
            const count = reviews.filter((r) => r.rating === star).length;
            const percentage = totalReviews > 0 ? (count / totalReviews) * 100 : 0;
            return (
              <div key={star} className="flex items-center gap-2 text-xs font-medium text-slate-600">
                <span className="w-6 flex items-center gap-1">
                  {star} <Star className="w-3 h-3 fill-amber-400 text-amber-400 inline" />
                </span>
                <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-emerald-500 rounded-full transition-all"
                    style={{ width: `${percentage}%` }}
                  />
                </div>
                <span className="w-6 text-right text-slate-400">{count}</span>
              </div>
            );
          })}
        </div>

        <div className="flex flex-col items-center justify-center text-center space-y-3">
          <p className="text-sm font-semibold text-slate-800">Have you used this product?</p>
          <button
            type="button"
            onClick={() => setShowReviewForm(!showReviewForm)}
            className="px-6 py-2.5 rounded-full bg-[#059669] hover:bg-[#047857] text-white text-xs font-bold shadow-md shadow-emerald-600/20 transition-all cursor-pointer"
          >
            {showReviewForm ? "Close Review Form" : "Write a Customer Review"}
          </button>
        </div>
      </div>

      {/* রিভিউ ফর্ম */}
      {showReviewForm && (
        <form
          onSubmit={handleSubmitReview}
          className="bg-white border border-emerald-100 p-6 sm:p-8 rounded-3xl shadow-sm space-y-5"
        >
          <h4 className="text-base font-bold text-slate-900">Share Your Experience</h4>

          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-slate-600">Your Overall Rating</label>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((s) => (
                <button
                  type="button"
                  key={s}
                  onMouseEnter={() => setHoverRating(s)}
                  onMouseLeave={() => setHoverRating(0)}
                  onClick={() => setRating(s)}
                  className="p-1 cursor-pointer"
                >
                  <Star
                    className={`w-6 h-6 transition-all ${
                      s <= (hoverRating || rating) ? "fill-amber-400 text-amber-400 scale-110" : "text-slate-300"
                    }`}
                  />
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-1">Your Name</label>
              <input
                type="text"
                placeholder="e.g., Sadia Rahman"
                value={authorName}
                onChange={(e) => setAuthorName(e.target.value)}
                className="w-full px-4 py-2 text-xs rounded-xl border border-slate-200 focus:outline-emerald-600 bg-slate-50/50"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-1">Review Headline</label>
              <input
                type="text"
                placeholder="e.g., Review for Product"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full px-4 py-2 text-xs rounded-xl border border-slate-200 focus:outline-emerald-600 bg-slate-50/50"
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-1">Your Review *</label>
            <textarea
              rows={4}
              required
              placeholder="What did you like or dislike about this product?"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className="w-full px-4 py-2.5 text-xs rounded-xl border border-slate-200 focus:outline-emerald-600"
            />
          </div>

          <div className="space-y-2">
            <label className="text-xs font-semibold text-slate-600 block">Attach Real Photos (Optional)</label>
            <div className="flex flex-wrap gap-3 items-center">
              {imageFiles.map((img, idx) => (
                <div key={idx} className="relative w-16 h-16 rounded-xl overflow-hidden border border-slate-200">
                  <Image src={img} alt="Preview" fill unoptimized className="object-cover" />
                  <button
                    type="button"
                    onClick={() => removeImage(idx)}
                    className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-0.5 hover:bg-red-600"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}

              <label className="w-16 h-16 rounded-xl border-2 border-dashed border-slate-300 hover:border-emerald-500 flex flex-col items-center justify-center cursor-pointer text-slate-400 hover:text-emerald-600 transition-colors">
                <Camera className="w-5 h-5" />
                <span className="text-[9px] font-bold mt-1">Upload</span>
                <input type="file" multiple accept="image/*" onChange={handleImageUpload} className="hidden" />
              </label>
            </div>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="px-6 py-2.5 rounded-full bg-[#059669] hover:bg-[#047857] text-white text-xs font-bold transition-all shadow-md cursor-pointer disabled:opacity-50"
          >
            {isSubmitting ? "Submitting..." : "Submit Review"}
          </button>
        </form>
      )}

      {/* রিভিউ তালিকা */}
      <div className="space-y-6">
        <h3 className="text-lg font-bold text-slate-900">Customer Feedback ({reviews.length})</h3>

        {isLoading ? (
          <p className="text-xs text-slate-400">Loading reviews...</p>
        ) : reviews.length === 0 ? (
          <p className="text-xs text-slate-400">No reviews yet. Be the first to review!</p>
        ) : (
          reviews.map((rev) => (
            <div key={rev.id} className="p-6 bg-white rounded-3xl border border-slate-100 shadow-sm space-y-4">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-slate-900">
                      {rev.customer_display_name || "Verified Customer"}
                    </span>
                    <span className="inline-flex items-center gap-1 text-[10px] text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full font-bold">
                      <CheckCircle className="w-3 h-3 text-emerald-600" /> Verified Purchase
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="flex gap-0.5 text-amber-400">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className={`w-3.5 h-3.5 ${
                            i < (rev.rating || 5) ? "fill-amber-400 text-amber-400" : "text-slate-200"
                          }`}
                        />
                      ))}
                    </div>
                    <span className="text-xs text-slate-400">• Verified Feedback</span>
                  </div>
                </div>
              </div>

              <div className="space-y-1">
                {rev.title && <h5 className="text-sm font-bold text-slate-900">{rev.title}</h5>}
                <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">{rev.content}</p>
              </div>

              {rev.images && rev.images.length > 0 && (
                <div className="flex gap-3 overflow-x-auto py-2">
                  {rev.images.map((imgSrc: string, i: number) => (
                    <div
                      key={i}
                      className="relative w-20 h-20 rounded-2xl overflow-hidden border border-slate-100 shrink-0"
                    >
                      <Image src={imgSrc} alt="Customer upload" fill unoptimized className="object-cover" />
                    </div>
                  ))}
                </div>
              )}

              {/* 🌟 মার্চেন্টের রিপ্লাই শো করার সেকশন */}
              {rev.replies && rev.replies.length > 0 && (
                <div className="space-y-2.5 pl-4 sm:pl-6 border-l-2 border-emerald-400/80 mt-4 pt-1">
                  {rev.replies.map((reply: any) => (
                    <div
                      key={reply.id}
                      className="bg-emerald-50/70 border border-emerald-100/80 p-3.5 rounded-2xl space-y-1"
                    >
                      <div className="flex items-center gap-2 flex-wrap">
                        <CornerDownRight className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                        <span className="text-xs font-bold text-slate-900">
                          {reply.user_name || "Store Owner"}
                        </span>
                        {reply.is_owner_reply && (
                          <span className="px-2 py-0.5 rounded-full bg-[#059669] text-white text-[9px] font-bold tracking-wide">
                            Store Owner
                          </span>
                        )}
                        <span className="text-[10px] text-slate-400">
                          • {reply.created_at ? String(reply.created_at).slice(0, 10) : "Recent"}
                        </span>
                      </div>
                      <p className="text-xs text-slate-700 pl-5 leading-relaxed font-medium">
                        {reply.content}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </section>
  );
}